import { Field } from 'formik';
import React, { FC, ReactNode } from 'react';
import AnimateHeight from 'react-animate-height';
import { useTranslation } from 'react-i18next';
import { Flex, StyledTag } from '../../../base/misc/misc';
import * as StyledFormikRadioTile from './formikRadioTile.styles';

const RadioTile = ({
	field: { name, value, onChange, onBlur },
	id,
	label,
	className,
	setFieldValue,
	Icon,
	price,
	estimatedPrice,
	priceSubLabel,
	renderTopRight,
	children,
	active,
	descriptionWords,
	estDeliveryTime,
	compact,
	...props
}) => {
	const { t } = useTranslation('common');
	return (
		<StyledFormikRadioTile.Wrapper
			checked={id === value}
			tabIndex="0"
			onKeyDown={e => {
				if (e.keyCode === 32 || e.keyCode === 13) {
					e.preventDefault();
					setFieldValue(name, id);
				}
			}}>
			<input
				name={name}
				id={id}
				type="radio"
				value={id}
				checked={id === value}
				onChange={onChange}
				onBlur={onBlur}
				{...props}
			/>
			<StyledFormikRadioTile.RadioTileLabel htmlFor={id} compact={compact}>
				<StyledFormikRadioTile.TileRadio checked={id === value} renderTopRight />
				<StyledFormikRadioTile.Top>
					<StyledFormikRadioTile.TopLeft>
						{Icon && (
							<StyledFormikRadioTile.IconWrapper>
								<Icon />
							</StyledFormikRadioTile.IconWrapper>
						)}
						{estDeliveryTime ? (
							<StyledFormikRadioTile.LabelWrapper>
								<StyledFormikRadioTile.LabelText>{label}</StyledFormikRadioTile.LabelText>
								<StyledFormikRadioTile.EstTime>{estDeliveryTime}</StyledFormikRadioTile.EstTime>
							</StyledFormikRadioTile.LabelWrapper>
						) : (
							<StyledFormikRadioTile.LabelText>{label}</StyledFormikRadioTile.LabelText>
						)}
					</StyledFormikRadioTile.TopLeft>
					{price && (
						<StyledFormikRadioTile.PriceWrapper>
							<StyledFormikRadioTile.PriceLabel>
								{t(estimatedPrice ? 'price-estimated' : 'price')}
								{priceSubLabel && `, ${priceSubLabel}`}
							</StyledFormikRadioTile.PriceLabel>
							<StyledFormikRadioTile.PriceValue>{price}</StyledFormikRadioTile.PriceValue>
						</StyledFormikRadioTile.PriceWrapper>
					)}
					{renderTopRight && renderTopRight(props)}
				</StyledFormikRadioTile.Top>
				{false && descriptionWords && (
					<StyledFormikRadioTile.StyledDescription>
						<Flex flexWrap="wrap" margin="1rem -0.5rem 0 -0.5rem">
							{descriptionWords.split(',').map((word, index) => (
								<StyledTag key={index}>{word}</StyledTag>
							))}
						</Flex>
					</StyledFormikRadioTile.StyledDescription>
				)}
			</StyledFormikRadioTile.RadioTileLabel>
			<AnimateHeight height={active ? 'auto' : 0}>
				<StyledFormikRadioTile.ChildWrapper>{children}</StyledFormikRadioTile.ChildWrapper>
			</AnimateHeight>
		</StyledFormikRadioTile.Wrapper>
	);
};

interface Props {
	id: string;
	name: string;
	label: string;
	setFieldValue: any;
	icon?: any;
	price?: string;
	estimatedPrice?: boolean;
	priceSubLabel?: string;
	active: boolean;
	descriptionWords?: string;
	estDeliveryTime?: string;
	renderTopRight?: any;
	compact?: boolean;
	disabled?: boolean;
	children?: ReactNode;
}

const FormikRadioTile: FC<Props> = ({ icon, price, estimatedPrice, priceSubLabel, disabled, compact, ...props }) => {
	return (
		<Field
			component={RadioTile}
			Icon={icon}
			price={price}
			estimatedPrice={estimatedPrice}
			priceSubLabel={priceSubLabel}
			compact={compact}
			{...props}
			disabled={disabled}
		/>
	);
};

export default FormikRadioTile;
